import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getBotConnections,
  getKillSwitch,
  setConnectionDisabled,
  setKillSwitch,
  getChannelClaudeTokenStatus,
  setChannelClaudeToken,
  getChannelProjectLinks,
  createChannelProjectLink,
  updateChannelProjectLink,
  deleteChannelProjectLink,
  getProjectQueueConfigs,
  getSreStats,
  upsertProjectQueueConfig,
  disableProjectQueueConfig,
  retryRepositoryProvisioning,
  getRoutedTasks,
  overrideTaskRouting,
  getPaperclipCatalog,
  refreshPaperclipCatalog,
  getPaperclipMembers,
  refreshPaperclipMembers,
  syncPaperclipRobotToken,
  provisionPaperclipMembers,
  reconcilePaperclipPersonAgents,
  resyncPaperclipPersonAgents,
  setPaperclipMemberAttribution,
  type BotAdminConnection,
  type KillSwitchState,
  type ChannelClaudeTokenState,
  type ChannelProjectLink,
  type ProjectQueueConfig,
  type SreStats,
  type UpsertProjectQueueConfigInput,
  type RoutedTask,
  type OverrideTaskRoutingInput,
  type PaperclipCatalog,
  type PaperclipMembersOverview,
} from '../services/api/botAdmin';
import { updateAppSettings } from '../services/api/admin';

/**
 * Phase 75 (Plan 75-05) — TanStack Query hooks for the bot-admin page.
 * Cloned from `useAppAccess.ts`'s pattern: mutations invalidate both query
 * keys on success only (server-confirm, no optimistic update — the kill
 * switch and per-user disable are both high-blast-radius actions where a
 * stale optimistic UI would be actively misleading).
 */

export function useBotConnections() {
  return useQuery<BotAdminConnection[]>({
    queryKey: ['bot-admin-connections'],
    queryFn: getBotConnections,
    staleTime: 30 * 1000,
  });
}

export function useKillSwitch() {
  return useQuery<KillSwitchState>({
    queryKey: ['bot-admin-kill-switch'],
    queryFn: getKillSwitch,
    staleTime: 30 * 1000,
  });
}

export function useSetConnectionDisabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, disabled }: { connectionId: number; disabled: boolean }) =>
      setConnectionDisabled(connectionId, disabled),
    onSuccess: () => {
      invalidateMany(queryClient, [['bot-admin-connections'], ['bot-admin-kill-switch']]);
    },
  });
}

export function useSetKillSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => setKillSwitch(enabled),
    onSuccess: () => {
      invalidateMany(queryClient, [['bot-admin-connections'], ['bot-admin-kill-switch']]);
    },
  });
}

/**
 * Phase 78 (Plan 78-05 / D-03 gap-closure) — present/absent status of the
 * shared channel-service Claude token.
 */
export function useChannelClaudeToken() {
  return useQuery<ChannelClaudeTokenState>({
    queryKey: ['bot-admin-channel-claude-token'],
    queryFn: getChannelClaudeTokenStatus,
    staleTime: 30 * 1000,
  });
}

/**
 * Phase 78 (Plan 78-05) — set/replace the shared channel-service Claude
 * token. Invalidates the status query on success so the badge flips to
 * "present" without a manual refetch.
 */
export function useSetChannelClaudeToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => setChannelClaudeToken(token),
    onSuccess: () => {
      invalidateMany(queryClient, [['bot-admin-channel-claude-token']]);
    },
  });
}

/**
 * Phase 79 (Plan 79-02 / D-2) — channel → project links. Server-confirm-only
 * mutations that invalidate the list on success (same pattern as the rest of
 * this file — no optimistic update).
 */
const CHANNEL_PROJECT_LINKS_KEY = ['bot-admin-channel-project-links'];

export function useChannelProjectLinks() {
  return useQuery<ChannelProjectLink[]>({
    queryKey: CHANNEL_PROJECT_LINKS_KEY,
    queryFn: getChannelProjectLinks,
    staleTime: 30 * 1000,
  });
}

export function useCreateChannelProjectLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      projectId,
      channelName,
    }: {
      channelId: string;
      projectId: number;
      channelName?: string;
    }) => createChannelProjectLink(channelId, projectId, channelName),
    onSuccess: () => {
      invalidateMany(queryClient, [CHANNEL_PROJECT_LINKS_KEY]);
    },
  });
}

export function useUpdateChannelProjectLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: number; projectId: number }) =>
      updateChannelProjectLink(id, projectId),
    onSuccess: () => {
      invalidateMany(queryClient, [CHANNEL_PROJECT_LINKS_KEY]);
    },
  });
}

export function useDeleteChannelProjectLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteChannelProjectLink(id),
    onSuccess: () => {
      invalidateMany(queryClient, [CHANNEL_PROJECT_LINKS_KEY]);
    },
  });
}

/**
 * Phase 80 (Plan 80-06 / BOTQ-09) — per-project bot work-queue config.
 * Server-confirm-only mutations that invalidate the list on success (same
 * pattern as the rest of this file — no optimistic update, per
 * T-80-06-STALE: a failed save must never show as applied).
 */
const PROJECT_QUEUE_CONFIG_KEY = ['bot-admin-project-queue-config'];

/** MKS-471 (Plan 128-07): how often the list refetches while a repository provisioning is pending. */
const PROVISIONING_REFETCH_MS = 5000;

export function useProjectQueueConfigs() {
  // A pending provisioning is followed to its result without a page reload; with nothing pending
  // there is no polling at all.
  return useQuery<ProjectQueueConfig[]>({
    queryKey: PROJECT_QUEUE_CONFIG_KEY,
    queryFn: getProjectQueueConfigs,
    staleTime: 30 * 1000,
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.repositoryProvisioning?.some((e) => e.status === 'pending'))
        ? PROVISIONING_REFETCH_MS
        : false,
  });
}

/**
 * Phase 127 (Plan 127-07 / R10; D-26) — the SRE agent's accuracy for one project. Only runs with a
 * project id; the server settles outcomes on every read, so a short stale time is enough.
 */
export function useSreStats(projectId: number | undefined) {
  return useQuery<SreStats>({
    queryKey: ['bot-admin-sre-stats', projectId],
    queryFn: () => getSreStats(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
}

export function useUpsertProjectQueueConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: number;
      input: UpsertProjectQueueConfigInput;
    }) => upsertProjectQueueConfig(projectId, input),
    onSuccess: () => {
      invalidateMany(queryClient, [PROJECT_QUEUE_CONFIG_KEY]);
    },
  });
}

/**
 * MKS-471 (Plan 128-07 / D-14 trigger 4) — Retry one linked repository's provisioning. Server-confirm
 * only: the list is refetched on success so the entry shows what the server now says.
 */
export function useRetryRepositoryProvisioning() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, repository }: { projectId: number; repository: string }) =>
      retryRepositoryProvisioning(projectId, repository),
    onSuccess: () => {
      invalidateMany(queryClient, [PROJECT_QUEUE_CONFIG_KEY]);
    },
  });
}

export function useDisableProjectQueueConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: number) => disableProjectQueueConfig(projectId),
    onSuccess: () => {
      invalidateMany(queryClient, [PROJECT_QUEUE_CONFIG_KEY]);
    },
  });
}

/**
 * Phase 112 (Plan 112-04 / PCROUTE-06, D-112-06) — routing read + audited
 * override. Server-confirm-only mutation that invalidates the routed-tasks
 * query on success (same pattern as the rest of this file — no optimistic
 * update, T-80-06-STALE: a failed override must never show as applied).
 */
function routedTasksKey(projectId: number | undefined) {
  return ['bot-admin-routed-tasks', projectId];
}

export function useRoutedTasks(projectId: number | undefined) {
  return useQuery<RoutedTask[]>({
    queryKey: routedTasksKey(projectId),
    queryFn: () => getRoutedTasks(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
}

export function useOverrideTaskRouting(projectId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: number; input: OverrideTaskRoutingInput }) =>
      overrideTaskRouting(taskId, input),
    onSuccess: () => {
      invalidateMany(queryClient, [routedTasksKey(projectId)]);
    },
  });
}

/**
 * Phase 115 (Plan 115-04 / PCPROV-01) — the stored, dated Factory™ catalog
 * backing the binding picker. `staleTime` is intentionally short (this is a
 * cheap, non-blocking read, D-115-03) but the query itself NEVER performs an
 * outbound call to Factory™ — it only ever reads the last-known snapshot.
 */
const PAPERCLIP_CATALOG_KEY = ['bot-admin-paperclip-catalog'];

export function usePaperclipCatalog() {
  return useQuery<PaperclipCatalog>({
    queryKey: PAPERCLIP_CATALOG_KEY,
    queryFn: getPaperclipCatalog,
    staleTime: 30 * 1000,
  });
}

/**
 * Requests a fresh catalog read. The mutation's own success means only "the
 * request was accepted" — the answer arrives later, asynchronously, over
 * the write-back relay (D-115-03). `onSuccess` therefore schedules a
 * RE-READ (invalidate) rather than assuming anything changed; a follow-up
 * `usePaperclipCatalog()` read may still show the pre-refresh snapshot until
 * the relay lands, and the admin UI's "Refresh" affordance can be clicked
 * again to re-check.
 */
export function useRefreshPaperclipCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, companyId }: { projectId: number; companyId?: string }) =>
      refreshPaperclipCatalog(projectId, companyId),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_CATALOG_KEY]);
    },
  });
}

/**
 * Phase 121 (Plan 121-03 / PCATTR-06, PCATTR-07) — the per-person Factory™
 * attribution overview: bound companies (last sync / request / failure) and every
 * person's state per company. A cheap stored read; it NEVER calls Factory™.
 */
const PAPERCLIP_MEMBERS_KEY = ['bot-admin-paperclip-members'];

export function usePaperclipMembers() {
  return useQuery<PaperclipMembersOverview>({
    queryKey: PAPERCLIP_MEMBERS_KEY,
    queryFn: getPaperclipMembers,
    staleTime: 30 * 1000,
  });
}

/**
 * Requests a fresh roster for one bound company. Success means only "the request
 * was accepted" — the answer arrives later, asynchronously, over the write-back
 * relay (as with `useRefreshPaperclipCatalog`). `onSuccess` therefore schedules a
 * RE-READ rather than assuming anything changed; the admin can press Refresh or
 * reload to check again.
 */
export function useRefreshPaperclipMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paperclipCompanyId: string) => refreshPaperclipMembers(paperclipCompanyId),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * MKS-275 — asks every bound company to rotate its ROBOT_CLAUDE_TOKEN secret now.
 * Success means only "the requests were accepted": the results arrive later,
 * asynchronously, over the write-back relay, exactly like the roster refresh above,
 * so `onSuccess` schedules a RE-READ rather than assuming the rows moved.
 */
export function useSyncPaperclipRobotToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncPaperclipRobotToken(),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * Flips one person's `attributionEnabled` opt-in. Server-confirm only (no
 * optimistic update): the backend refuses to enable a row that is not an active,
 * non-viewer, fresh member (409), and a stale optimistic UI would hide that.
 */
export function useSetPaperclipMemberAttribution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mappingId,
      attributionEnabled,
    }: {
      mappingId: number;
      attributionEnabled: boolean;
    }) => setPaperclipMemberAttribution(mappingId, attributionEnabled),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * The global `paperclip_attribution_enabled` switch, written through the generic
 * settings PATCH, then the overview (which reports `switchOn`) is re-read.
 */
export function useSetPaperclipAttributionSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => updateAppSettings({ paperclip_attribution_enabled: enabled }),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * Phase 122 (PCTOK-09, D-16) — the `paperclip_token_sync_enabled` operational
 * off-switch, written through the generic settings PATCH like the attribution
 * switch, then the overview (which reports `tokenSync.switchOn`) is re-read.
 */
export function useSetPaperclipTokenSyncSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => updateAppSettings({ paperclip_token_sync_enabled: enabled }),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * Phase 124 (PCMEMB-09, D-11) — the `paperclip_member_provisioning_enabled` operational
 * off-switch, written through the generic settings PATCH like the token-sync switch, then
 * the overview (which reports `provision.switchOn`) is re-read.
 */
export function useSetPaperclipProvisioningSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      updateAppSettings({ paperclip_member_provisioning_enabled: enabled }),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * Phase 124 (PCMEMB-09, PCMEMB-11, D-11) — request provisioning for one person or for
 * everyone missing. Success means only "the requests were accepted": accounts are created
 * asynchronously, so the overview is re-read and shows `Requested` until the result lands.
 */
export function useProvisionPaperclipMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { paperclipCompanyId: string; userId?: number }) =>
      provisionPaperclipMembers(input),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * Phase 125 (PCAGT-13, D-13, D-17) — the `paperclip_person_agents_enabled` switch, written
 * through the generic settings PATCH like the other Factory™ switches, then the overview
 * (which reports `personAgents.switchOn`) is re-read. Dark by default; one switch per
 * environment, so one per Factory™ company.
 */
export function useSetPaperclipPersonAgentsSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      updateAppSettings({ paperclip_person_agents_enabled: enabled }),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * Phase 125 (PCAGT-09, D-12, D-17) — admin Reconcile of person agents (one person or the whole
 * company). Success means the lifecycle requests were enqueued; the agent state changes when the
 * plugin answers, so the overview is re-read.
 */
export function useReconcilePaperclipPersonAgents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { paperclipCompanyId: string; userId?: number }) =>
      reconcilePaperclipPersonAgents(input),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}

/**
 * Phase 125 (PCAGT-10, D-06, D-17) — request one company re-sync of the person agents. Success
 * means only "accepted": the counts arrive asynchronously, so the overview is re-read.
 */
export function useResyncPaperclipPersonAgents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paperclipCompanyId: string) => resyncPaperclipPersonAgents(paperclipCompanyId),
    onSuccess: () => {
      invalidateMany(queryClient, [PAPERCLIP_MEMBERS_KEY]);
    },
  });
}
